import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams, useLocation, useNavigate } from 'react-router-dom'
import { formValidator } from '../../../../helpers'
import { InputGroup, Modal, Button } from '../../../../UI'
import { createAdmin, updateAdmin } from '../../../../store/actions'
import { axios } from '../../../../lib'
import Container from './styles'

const AdminForm = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { state } = useLocation()
  const params = useParams()
  const { adminLists } = useSelector((s) => s.AdminData)
  const [currentStep, setCurrentStep] = useState(1)
  const [confirm_password_error, setConfirm_password_error] = useState(false)
  const [password_error, setPassword_error] = useState(false)
  const [emailError, setEmailError] = useState('')
  const [loading, setLoading] = useState(false)
  const [checkingEmail, setCheckingEmail] = useState(false)
  const [formData, setFormData] = useState(() => {
    if (adminLists && state && state.adminId) {
      return adminLists.data.find((item) => item.id === state.adminId)
    } else {
      return {
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        password: '',
        role: 'ADMIN',
      }
    }
  })

  const handleInput = ({ target: { value, name } }) => {
    setFormData((s) => ({
      ...s,
      [name]: value,
    }))
    
    // Clear email error when user types
    if (name === 'email' && emailError) {
      setEmailError('')
    }
  }

  const handleClose = () => {
    navigate('/dashboard/admins')
  }

  const checkEmailExists = async (email) => {
    try {
      setCheckingEmail(true)
      const response = await axios.get(`/admin/admins/check-email?email=${encodeURIComponent(email)}`)
      return response.data.exists
    } catch (error) {
      console.error('Error checking email:', error)
      return false
    } finally {
      setCheckingEmail(false)
    }
  }

  const validateStep1 = async () => {
    // Validate required fields
    const step1Fields = ['firstName', 'lastName', 'email', 'phone']
    let isValid = true
    
    for (const field of step1Fields) {
      if (!formData[field] || formData[field].trim() === '') {
        isValid = false
        break
      }
    }
    
    if (!isValid) {
      setEmailError('Please fill in all fields')
      return false
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!emailRegex.test(formData.email)) {
      setEmailError('Please enter a valid email address')
      return false
    }
    
    // Check if email exists (only for new admins)
    if (params.action === 'add') {
      const emailExists = await checkEmailExists(formData.email)
      if (emailExists) {
        setEmailError('This email is already registered')
        return false
      }
    }
    
    return true
  }

  const handleNext = async () => {
    const isValid = await validateStep1()
    if (isValid) {
      setCurrentStep(2)
    }
  }

  const handleBack = () => {
    setCurrentStep(1)
  }

  const handleSubmit = (e) => {
    if (
      formValidator(document.forms['admin-form'].getElementsByTagName('input'), setConfirm_password_error, setPassword_error )
      ) {
      setLoading(true)
      
      const action = params.action === 'add' ? createAdmin : updateAdmin
      const apiCall = params.action === 'add' 
        ? dispatch(action(formData))
        : dispatch(action(state?.adminId, formData))
      
      apiCall
        .then((response) => {
          setLoading(false)
            handleClose()
        })
        .catch((error) => {
          setLoading(false)
          // Check for duplicate email error
          if (error?.response?.data?.message?.includes('email')) {
            setEmailError(error.response.data.message || 'This email is already registered')
            setCurrentStep(1) // Go back to step 1 to show the error
          }
        })
    }
  }

  return (
    <Container>
      <Modal showModal={true} onClose={handleClose}>
        <div className="adminForm--container">
          <header>
            <h2>
              {params.action === 'resetPassword'
                ? 'Reset Password'
                : params.action !== 'add'
                ? 'Edit Admin'
                : 'Add New Admin'}
            </h2>
            <p>
              {params.action === 'add' && `Step ${currentStep} of 2`}
            </p>
          </header>

          {/* Progress Indicator */}
          {params.action === 'add' && (
            <div className="progress-indicator">
              <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
                <div className="step-number">1</div>
                <span>Basic Info</span>
              </div>
              <div className="step-line"></div>
              <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
                <div className="step-number">2</div>
                <span>Security</span>
              </div>
            </div>
          )}

          <form
            noValidate
            name="admin-form"
            onSubmit={(e) => {
              e.preventDefault()
              if (currentStep === 1 && params.action === 'add') {
                handleNext()
              } else {
                handleSubmit()
              }
            }}
          >
            {/* Step 1: Basic Information */}
            {currentStep === 1 && (
              <>
                <div className="input-row">
                  <InputGroup
                    name="firstName"
                    label={'First name'}
                    onChange={handleInput}
                    value={formData.firstName}
                    data-label="First name"
                    disabled={params.action === 'resetPassword'}
                    required
                    placeholder="Enter the first name"
                  />
                  <InputGroup
                    name="lastName"
                    label={'Last Name'}
                    onChange={handleInput}
                    value={formData.lastName}
                    data-label="Last name"
                    required
                    disabled={params.action === 'resetPassword'}
                    placeholder="Enter the last name"
                  />
                </div>
                
                {params.action !== 'resetPassword' && (
                  <>
                    <InputGroup
                      label={'Email address'}
                      name="email"
                      value={formData.email}
                      onChange={handleInput}
                      required
                      data-label="Email"
                      type="email"
                      placeholder="Enter the email address"
                      error={emailError}
                    />
                    {emailError && (
                      <p className="error-message">{emailError}</p>
                    )}
                    
                    <InputGroup
                      name="phone"
                      label={'Phone number'}
                      data-label="Phone number"
                      value={formData.phone}
                      required
                      type="tel"
                      onChange={handleInput}
                      placeholder="Enter the phone number"
                    />
                  </>
                )}
                
                {params.action === 'add' && (
                  <Button 
                    type="button" 
                    onClick={handleNext} 
                    loading={checkingEmail}
                    spinnerWithTxt 
                    full
                  >
                    {checkingEmail ? 'Checking email...' : 'Next'}
                  </Button>
                )}
              </>
            )}

            {/* Step 2: Security & Role */}
            {(currentStep === 2 || params.action !== 'add') && (
              <>
                {params.action !== 'resetPassword' && (
                  <div className="input-group">
                    <label htmlFor="role">Role</label>
                    <select
                      id="role"
                      name="role"
                      value={formData.role}
                      onChange={handleInput}
                      className="select-input"
                      required
                    >
                      <option value="ADMIN">Admin</option>
                      <option value="SUPER_ADMIN">Super Admin</option>
                      <option value="MODERATOR">Moderator</option>
                    </select>
                  </div>
                )}
                
                <InputGroup
                  label={
                    params.action === 'resetPassword' ? 'New Password' : 'Password'
                  }
                  name="password"
                  type="password"
                  required
                  value={formData.password}
                  onChange={handleInput}
                  error={password_error}
                  seterror={setPassword_error}
                  placeholder="Enter the password"
                />
                
                <div className="button-row">
                  {params.action === 'add' && currentStep === 2 && (
                    <Button 
                      type="button" 
                      onClick={handleBack}
                      className="btn-secondary"
                    >
                      Back
                    </Button>
                  )}
                  
                  <Button 
                    type="submit" 
                    spinnerWithTxt 
                    loading={loading} 
                    full={params.action !== 'add' || currentStep !== 2}
                  >
                    {params.action === 'resetPassword' ? 'Reset Password' : 'Submit'}
                  </Button>
                </div>
              </>
            )}
          </form>
        </div>
      </Modal>
    </Container>
  )
}

export default AdminForm
